"use client";

import type { HeroComponent } from "../../types";
import { RuntimeImage, useLocale } from "../../runtime/RendererRuntime";
import { t } from "../../i18n/strings";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, resolveImageValue } from "../../utils/imageOptions";

/** Hero component renderer with background image, title and subtitle. */
export function HeroRenderer({ component }: { component: HeroComponent }) {
  const locale = useLocale();
  const { title, subtitle, body, backgroundImageSrc, variant = "overlay", eyebrow, imageAlt } = component.content;
  const { src, options } = resolveImageValue(backgroundImageSrc);
  const backgroundSrc = src || "/media/welcome_background.jpg";
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);

  const renderSectionTitle = () => {
    const protectedPhrase = "du Congo";
    if (!title.includes(protectedPhrase)) return title;
    const [before, ...after] = title.split(protectedPhrase);
    return (
      <>
        {before}
        <span className="whitespace-nowrap">{protectedPhrase}</span>
        {after.join(protectedPhrase)}
      </>
    );
  };

  if (variant === "texture") {
    return (
      <section className="bcc-section-hero relative left-1/2 mb-8 w-screen max-w-[100vw] -translate-x-1/2 overflow-hidden bg-[#EEF2F5] text-[#005CA9] md:mb-10">
        <div className="absolute inset-0" aria-hidden="true">
          <RuntimeImage
            src={backgroundSrc}
            alt=""
            className={mergeClassNames(
              "absolute inset-y-0 right-0 h-full w-full object-cover grayscale-[0.42] saturate-[0.68] contrast-[1.04] md:w-[66%]",
              overrideProps.className,
            )}
            style={{ ...objectPositionStyle, ...overrideProps.style }}
            priority
          />
          <div
            className="absolute inset-0 hidden md:block"
            style={{
              background:
                "linear-gradient(90deg, #EEF2F5 0%, #EEF2F5 39%, rgba(238,242,245,0.97) 49%, rgba(238,242,245,0.74) 62%, rgba(238,242,245,0.10) 100%)",
            }}
          />
          <div
            className="absolute inset-0 md:hidden"
            style={{ background: "linear-gradient(180deg, rgba(238,242,245,0.92) 0%, rgba(238,242,245,0.98) 100%)" }}
          />
          {/* Filet de pied du hero : meme jeton que la bordure des panneaux
              (`bcc-panel`), pour que la separation soit aussi lisible ici que
              sur les cartes. Le gris translucide precedent disparaissait. */}
          <div className="absolute inset-x-0 bottom-0 h-px bg-bcc-panel" />
        </div>
        <div className="relative mx-auto flex min-h-[305px] max-w-7xl items-center px-5 py-10 sm:px-8 md:min-h-[320px] md:py-11 lg:min-h-[330px] lg:px-16">
          <div className="w-full max-w-[39rem] md:w-[55%] lg:w-[52%]">
            {eyebrow ? (
              <p className="flex items-center gap-3 text-xs font-bold uppercase tracking-[0.2em] text-[#FFB323] sm:text-sm">
                <span className="h-0.5 w-8 shrink-0 bg-[#FFB323]" aria-hidden="true" />
                <span>{eyebrow}</span>
              </p>
            ) : null}
            <h1
              className="mt-4 max-w-[21ch] text-[2.25rem] font-bold leading-[1.06] text-[#005CA9] sm:text-[2.55rem] lg:text-[2.8rem] xl:text-[2.95rem]"
              style={{ fontFamily: "var(--font-aleo)" }}
            >
              {renderSectionTitle()}
            </h1>
            {subtitle ? (
              <p className="mt-5 max-w-2xl text-xl font-semibold leading-8 text-[#173D66]">{subtitle}</p>
            ) : null}
            {/* Interligne resserré sous 640px (24px pour 16px de corps) : le format aéré du
                héros reste en vigueur dès sm, où la ligne porte assez de mots. */}
            {body ? (
              <p className="mt-5 max-w-[37rem] text-base leading-6 text-[#506781] sm:text-[1.05rem] sm:leading-8">{body}</p>
            ) : null}
          </div>
        </div>
      </section>
    );
  }

  if (variant === "split") {
    return (
      <section className="bcc-section-hero relative left-1/2 w-screen max-w-[100vw] -translate-x-1/2 border-y border-slate-200 bg-[#F1F7FB]">
        <div className="mx-auto grid max-w-7xl lg:min-h-[390px] lg:grid-cols-[minmax(0,1.08fr)_minmax(360px,0.92fr)]">
          <div className="flex min-w-0 items-center px-5 py-12 sm:px-8 md:py-16 lg:px-10 lg:py-20">
            <div className="max-w-3xl">
              {eyebrow ? (
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E98A00]">{eyebrow}</p>
              ) : null}
              <h1
                className="mt-3 text-4xl font-bold leading-[1.08] text-[#005CA9] sm:text-5xl lg:text-6xl"
                style={{ fontFamily: "var(--font-aleo)" }}
              >
                {title}
              </h1>
              <div className="mt-5 h-1 w-12 rounded-sm bg-[#FFA401]" aria-hidden="true" />
              {subtitle ? (
                <p className="mt-6 text-xl font-semibold leading-8 text-[#0F2849]">{subtitle}</p>
              ) : null}
              {body ? (
                <p className="mt-4 max-w-2xl text-base leading-7 text-[#526987] sm:text-lg sm:leading-8">{body}</p>
              ) : null}
            </div>
          </div>
          <div className="relative min-h-64 overflow-hidden border-t border-slate-200 lg:min-h-full lg:border-l lg:border-t-0">
            <RuntimeImage
              src={backgroundSrc}
              alt={imageAlt?.trim() || ""}
              className={mergeClassNames("absolute inset-0 h-full w-full object-cover", overrideProps.className)}
              style={{ ...objectPositionStyle, ...overrideProps.style }}
              loading="lazy"
            />
          </div>
        </div>
      </section>
    );
  }

  // On mobile the body collapses to its first sentence behind a native
  // <details> toggle — no JS state, so it works even before hydration.
  // Desktop always shows the full text.
  const firstSentenceMatch = body.match(/[^.?!]+[.?!]/);
  const previewSentence = firstSentenceMatch ? firstSentenceMatch[0].trim() : body;
  const hasAdditionalContent = previewSentence.length < body.length;
  const previewWithEllipsis = hasAdditionalContent
    ? `${previewSentence.replace(/[.?!]+$/, "").trim()}...`
    : previewSentence;
  return (
    <section className="relative z-0 isolate">
      <div className={mergeClassNames("relative w-full bg-[#0069B4]", overrideProps.className)} style={overrideProps.style}>
        <div className="absolute inset-0">
          <RuntimeImage
            src={backgroundSrc}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
            style={objectPositionStyle}
            loading="lazy"
          />
          <div className="absolute inset-0" style={{ background: 'lab(17 20.5 -52.23 / 0.9)' }} aria-hidden="true" />
        </div>
        <div className="relative max-w-7xl px-4 lg:px-8">
          <div className="flex items-center py-10 sm:py-12 md:py-16">
            <div className="text-white">
              {/* Keep "Centrale du Congo" on one line: a break inside the
                  institution's name reads badly at the widths where the title
                  wraps. Applies only when the phrase appears verbatim. */}
              <h1 className="text-2xl sm:text-3xl md:text-4xl text-[#79D4FF] text-left font-semibold" style={{ fontFamily: 'var(--font-aleo)' }}>{(() => {
                const full = title;
                const h = "Centrale du Congo";
                if (full.includes(h)) {
                  const parts = full.split(h);
                  return <>{parts[0]}<span className="whitespace-nowrap">{h}</span>{parts[1]}</>;
                }
                return full;
              })()}</h1>
              <p className="mt-3 text-xl md:text-2xl leading-snug md:mt-4 md:leading-normal" style={{ fontFamily: "Aleo" }}>
                {subtitle}
              </p>
              <div
                className="mt-3 text-[16px] xl:text-[18px] leading-snug sm:leading-5 text-white/70 md:mt-4 md:text-[16px] md:leading-relaxed"
                style={{ fontFamily: "Nunito Sans" }}
              >
                <p className="hidden md:block">{body}</p>
                {hasAdditionalContent ? (
                  <details className="group md:hidden">
                    <summary className="cursor-pointer list-none text-left leading-snug text-white/80 [&::-webkit-details-marker]:hidden">
                      <span className="group-open:hidden">
                        {previewWithEllipsis}{" "}
                      </span>
                      <span className="inline-flex items-center rounded-full py-0.5 text-xs xl:text-sm font-semibold text-[#79D4FF] transition hover:text-white hover:ring-white/60 group-open:hidden">
                        {t(locale, "showMore")}
                      </span>
                    </summary>
                    <div className="mt-2 hidden text-white/80 group-open:block">{body}</div>
                  </details>
                ) : (
                  <p className="md:hidden">{body}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
